import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Briefcase, Bell, BellPlus, X, Lock, CheckCircle } from 'lucide-react';
import { jobApplicationApi } from '@/lib/api/jobApplicationApi';
import { prerequisiteApi, RequiredModulesStatus } from '@/lib/api/prerequisiteApi';
import { needSubscriptionApi } from '@/lib/api/needSubscriptionApi';
import { PublicNeed, PublicNeedQuery, ApplicationStatus } from '@/lib/models/jobApplication';
import { NeedSubscription, subscriptionDisplayName } from '@/lib/models/needSubscription';
import {
    jobFiltersActive,
    filtersToCriteria,
    criteriaToFilters,
    searchSignature,
    type JobFilters,
    type JobSort,
    type JobSearchCriteria,
} from '@/lib/jobMatching';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { NEXILINK_SPACING, NEXILINK_CARD_WHITE, NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import NeedCard from '@/components/needs/NeedCard';
import { publicNeedToView } from '@/components/needs/NeedView';
import { ApplicationStatusLabels } from '@/lib/models/jobApplication';
import JobFilterBar from './JobFilterBar';
import BevakningEditorModal from './BevakningEditorModal';
import { loadJobBoardPrefs, saveJobBoardPrefs } from './jobBoardPrefs';

// Backend page size for the job board (#39). Filtering, sorting and pagination
// all happen server-side; "Ladda fler" fetches and appends the next page.
const JOB_PAGE_SIZE = 12;

const JobBoardPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [needs, setNeeds] = useState<PublicNeed[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [prereqStatuses, setPrereqStatuses] = useState<Record<string, RequiredModulesStatus>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // Immediate input vs. debounced value that actually drives the query — so we
    // don't fire a backend request on every keystroke.
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    // Filters + sort are restored from localStorage so the candidate's search
    // settings persist across visits (#39). "Rensa allt" resets to defaults.
    const [filters, setFilters] = useState<JobFilters>(() => loadJobBoardPrefs().filters);
    const [sort, setSort] = useState<JobSort>(() => loadJobBoardPrefs().sort);
    // Filter dropdown options come from the backend facets endpoint (distinct
    // cities/competencies across ALL active needs), so they stay stable as the
    // candidate narrows the search.
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [competencyOptions, setCompetencyOptions] = useState<string[]>([]);
    const [subscriptions, setSubscriptions] = useState<NeedSubscription[]>([]);
    // Naming dialog for "Bevaka sökning" — created bevakningar get a name (2.0).
    const [showSaveModal, setShowSaveModal] = useState(false);
    // need_id -> application status, so the board can mark already-applied jobs.
    const [appliedByNeed, setAppliedByNeed] = useState<Record<string, ApplicationStatus>>({});

    // Debounce the free-text search: 300ms after the last keystroke, commit it to
    // searchQuery, which triggers the backend refetch below.
    useEffect(() => {
        const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    // One-time loads that don't depend on the filter query.
    useEffect(() => {
        loadFacets();
        loadSubscriptions();
        loadMyApplications();
    }, []);

    // "Visa matchande tjänster" on the Bevakningar tab navigates here with the
    // saved search in location.state — apply it to the search box + filter bar,
    // then clear the state so a later back/refresh doesn't re-apply it.
    useEffect(() => {
        const applySearch = (location.state as any)?.applySearch as
            | { query?: string; criteria?: JobSearchCriteria }
            | undefined;
        if (!applySearch) return;
        setSearchInput(applySearch.query ?? '');
        setFilters(criteriaToFilters(applySearch.criteria));
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist filter/sort settings whenever they change.
    useEffect(() => {
        saveJobBoardPrefs({ filters, sort });
    }, [filters, sort]);

    // Build the backend query for a given page from the current search/filters/sort.
    const buildQuery = (targetPage: number): PublicNeedQuery => ({
        page: targetPage,
        limit: JOB_PAGE_SIZE,
        q: searchQuery || undefined,
        city: filters.city || undefined,
        employment_type: filters.employmentType || undefined,
        employment_extent: filters.extent || undefined,
        work_location: filters.workLocation || undefined,
        competencies: filters.competencies.length ? filters.competencies : undefined,
        supervision: filters.supervisionOnly || undefined,
        sort,
    });

    // Refetch page 1 (replacing the list) whenever the search, filters or sort
    // change. Keyed on a serialized snapshot so it fires exactly on real changes;
    // the effect body reads the matching fresh state.
    const queryKey = JSON.stringify({ searchQuery, filters, sort });
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        jobApplicationApi
            .listPublicNeeds(buildQuery(1))
            .then((res) => {
                if (cancelled) return;
                setNeeds(res.needs);
                setTotal(res.total);
                setPage(1);
                loadPrereqStatuses(res.needs);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);

    const handleLoadMore = async () => {
        if (isLoadingMore) return;
        const next = page + 1;
        setIsLoadingMore(true);
        try {
            const res = await jobApplicationApi.listPublicNeeds(buildQuery(next));
            setNeeds((prev) => [...prev, ...res.needs]);
            setTotal(res.total);
            setPage(next);
            loadPrereqStatuses(res.needs);
        } finally {
            setIsLoadingMore(false);
        }
    };

    // Fetch prerequisite status for a batch of needs and merge into the map. Runs
    // for each fetched page (initial + every "Ladda fler"). Uses the course/module
    // based required-modules status (#217) — the same source the job detail page
    // and the employer "Förkunskap" row use — NOT the legacy case-based endpoint.
    const loadPrereqStatuses = async (list: PublicNeed[]) => {
        const entries = await Promise.all(
            list.map(async (need) => {
                try {
                    const status = await prerequisiteApi.getRequiredModulesStatus(need.id);
                    return status.total > 0 ? ([need.id, status] as const) : null;
                } catch {
                    return null; // User may not be authenticated — skip
                }
            }),
        );
        setPrereqStatuses((prev) => {
            const nextMap = { ...prev };
            entries.forEach((e) => {
                if (e) nextMap[e[0]] = e[1];
            });
            return nextMap;
        });
    };

    const loadFacets = async () => {
        const f = await jobApplicationApi.getNeedFacets();
        setCityOptions(f.cities);
        setCompetencyOptions(f.competencies);
    };

    const loadMyApplications = async () => {
        const res = await jobApplicationApi.getMyApplications(1, 100);
        const map: Record<string, ApplicationStatus> = {};
        (res.applications ?? []).forEach((a) => {
            // A withdrawn application means the candidate can re-apply — don't mark it.
            if (a.status !== 'withdrawn') map[a.need_id] = a.status;
        });
        setAppliedByNeed(map);
    };

    const loadSubscriptions = async () => {
        setSubscriptions(await needSubscriptionApi.listMySubscriptions());
    };

    // The current search = free-text + the structured filter facets. Its signature
    // lets us tell whether this exact search (text AND filters) is already watched,
    // regardless of competency ordering (#39 Phase 2).
    const normalisedQuery = searchInput.trim();
    const currentCriteria = filtersToCriteria(filters);
    const currentSignature = searchSignature(normalisedQuery, currentCriteria);
    const alreadyWatched = subscriptions.some(
        (s) => searchSignature(s.query, s.criteria ?? {}) === currentSignature
    );

    // Open the naming dialog (bevakningar 2.0) seeded with the current search, so
    // the candidate can name the bevakning before it's created.
    const handleSaveWatch = () => {
        if (alreadyWatched) return;
        setShowSaveModal(true);
    };

    const handleWatchSaved = (created: NeedSubscription) => {
        setSubscriptions((prev) =>
            prev.some((s) => s.id === created.id)
                ? prev.map((s) => (s.id === created.id ? created : s))
                : [created, ...prev],
        );
        setShowSaveModal(false);
    };

    // Re-apply a saved search back into the live filter bar + search box.
    const applySavedSearch = (s: NeedSubscription) => {
        setSearchInput(s.query);
        setFilters(criteriaToFilters(s.criteria));
    };

    const handleRemoveWatch = async (id: string) => {
        const prev = subscriptions;
        setSubscriptions((s) => s.filter((x) => x.id !== id)); // optimistic
        try {
            await needSubscriptionApi.deleteSubscription(id);
        } catch (e) {
            console.error('[JobBoardPage] Failed to remove watch:', e);
            setSubscriptions(prev); // revert
        }
    };

    const anyFilterActive = jobFiltersActive(filters) || normalisedQuery.length > 0;
    const hasMore = needs.length < total;

    // Full-page loader only on the very first load; later refetches (filter/sort
    // changes) keep the current results visible until the new page arrives.
    if (isLoading && needs.length === 0) {
        return <LoadingState message="Laddar lediga tjänster..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {/* Search + Bevaka — sticky so the filters/search stay put while only the
                job list scrolls (main is the scroll container). */}
            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} p-4 sticky top-0 z-20`}>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Sök på titel, roll, stad eller kompetens..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        />
                    </div>
                    <button
                        onClick={handleSaveWatch}
                        disabled={alreadyWatched}
                        title={
                            alreadyWatched
                                ? 'Den här sökningen bevakas redan'
                                : normalisedQuery || jobFiltersActive(filters)
                                    ? 'Bevaka den här sökningen (text + filter) och få notis vid nya träffar'
                                    : 'Bevaka alla nya annonser'
                        }
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                            alreadyWatched
                                ? 'bg-gray-100 text-gray-500 cursor-default'
                                : 'bg-[#1A4D4F] text-white hover:bg-[#143c3e] disabled:opacity-60'
                        }`}
                    >
                        {alreadyWatched ? <Bell className="w-4 h-4" /> : <BellPlus className="w-4 h-4" />}
                        {alreadyWatched ? 'Bevakas' : 'Bevaka sökning'}
                    </button>
                </div>

                {/* Structured filters + sort (#39, Phase 1) */}
                <JobFilterBar
                    filters={filters}
                    onFiltersChange={setFilters}
                    sort={sort}
                    onSortChange={setSort}
                    cityOptions={cityOptions}
                    competencyOptions={competencyOptions}
                    resultCount={total}
                />

                {/* Saved searches ("Mina bevakningar") — click a chip to re-apply its
                    text + filters to the board; the × removes it. The chip matching
                    the current search is highlighted. */}
                {subscriptions.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                            <Bell className="w-3.5 h-3.5" /> Dina bevakningar:
                        </span>
                        {subscriptions.map((s) => {
                            const isActive = searchSignature(s.query, s.criteria ?? {}) === currentSignature;
                            return (
                                <span
                                    key={s.id}
                                    className={`inline-flex items-center rounded-full text-xs font-medium border ${
                                        isActive
                                            ? 'bg-[#1A4D4F] text-white border-[#1A4D4F]'
                                            : 'bg-[#A4C3B2]/30 text-[#1A4D4F] border-transparent'
                                    }`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => applySavedSearch(s)}
                                        className={`pl-3 pr-1.5 py-1 rounded-l-full transition-colors ${
                                            isActive ? 'hover:bg-white/10' : 'hover:bg-[#1A4D4F]/10'
                                        }`}
                                        title="Använd denna bevakning"
                                    >
                                        {subscriptionDisplayName(s)}
                                    </button>
                                    <button
                                        onClick={() => handleRemoveWatch(s.id)}
                                        className={`pr-1.5 pl-0.5 py-1 rounded-r-full ${
                                            isActive ? 'hover:bg-white/10' : 'hover:bg-[#1A4D4F]/10'
                                        }`}
                                        title="Ta bort bevakning"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Results */}
            {needs.length > 0 ? (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {needs.map((need) => {
                        const applied = appliedByNeed[need.id] || null;
                        // Present only when the job actually has prerequisites (the
                        // loader skips total === 0). Surface it either way: amber when
                        // there's still something to complete, green when already met —
                        // so the candidate sees the requirement AND their standing on it.
                        const ps = prereqStatuses[need.id] || null;
                        return (
                            <NeedCard
                                key={need.id}
                                data={publicNeedToView(need)}
                                badge={applied ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#1A4D4F]/10 text-[#1A4D4F] font-medium whitespace-nowrap">{ApplicationStatusLabels[applied]}</span> : undefined}
                                meta={ps ? (
                                    ps.all_met ? (
                                        <span className="text-[11px] flex items-center gap-1 text-green-600 whitespace-nowrap" title="Du uppfyller förkunskapskraven">
                                            <CheckCircle className="w-3 h-3" /> Förkunskap klar
                                        </span>
                                    ) : (
                                        <span className="text-[11px] flex items-center gap-1 text-amber-600 whitespace-nowrap" title="Förkunskapskrav kvar att genomföra">
                                            <Lock className="w-3 h-3" /> Förkunskap {ps.completed}/{ps.total}
                                        </span>
                                    )
                                ) : undefined}
                                onClick={() => navigate(`/dashboard/jobs/${need.id}`)}
                            />
                        );
                    })}
                </div>
                {hasMore && (
                    <div className="flex justify-center pt-2">
                        <button
                            type="button"
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:border-[#1A4D4F] hover:text-[#1A4D4F] transition-colors disabled:opacity-60"
                        >
                            {isLoadingMore ? 'Laddar…' : 'Ladda fler'}
                            <span className="text-gray-400">({total - needs.length})</span>
                        </button>
                    </div>
                )}
                </>
            ) : (
                <div className={`${NEXILINK_CARD_WHITE} p-12 text-center`}>
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2] rounded-full flex items-center justify-center">
                        <Briefcase className="w-8 h-8 text-[#1A4D4F]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {anyFilterActive ? 'Inga träffar' : 'Inga lediga tjänster just nu'}
                    </h3>
                    <p className="text-gray-600">
                        {anyFilterActive ? 'Prova att ändra din sökning eller dina filter' : 'Nya tjänster publiceras löpande — kom tillbaka snart!'}
                    </p>
                    {(searchQuery || jobFiltersActive(filters)) && !alreadyWatched && (
                        <button
                            onClick={handleSaveWatch}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-[#1A4D4F] text-white rounded-xl text-sm font-medium hover:bg-[#143c3e]"
                        >
                            <BellPlus className="w-4 h-4" />
                            Bevaka den här sökningen och få notis vid nya träffar
                        </button>
                    )}
                </div>
            )}

            {showSaveModal && (
                <BevakningEditorModal
                    initialQuery={normalisedQuery}
                    initialFilters={filters}
                    onClose={() => setShowSaveModal(false)}
                    onSaved={handleWatchSaved}
                />
            )}
        </div>
    );
};

export default JobBoardPage;
